import React, { useMemo, useState } from 'react';
import {
  Alert, Box, Button, Card, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  Grid, LinearProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography, useTheme,
} from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { LineChart } from '@mui/x-charts/LineChart';
import { useTranslation } from 'react-i18next';

import type {
  GrowthSummary,
  RoomBookingMonthlyRetention,
  RoomBookingRetentionUser,
} from '../../models/Growth';

type DetailKind = 'retained' | 'churned' | 'new' | 'reactivated';

interface DetailDialogState {
  kind: DetailKind;
  month: string;
  users: RoomBookingRetentionUser[];
}

const detailConfig: Record<DetailKind, { countKey: keyof RoomBookingMonthlyRetention; detailsKey: keyof RoomBookingMonthlyRetention }> = {
  retained: { countKey: 'retainedUsers', detailsKey: 'retainedUserDetails' },
  churned: { countKey: 'churnedUsers', detailsKey: 'churnedUserDetails' },
  new: { countKey: 'newUsers', detailsKey: 'newUserDetails' },
  reactivated: { countKey: 'reactivatedUsers', detailsKey: 'reactivatedUserDetails' },
};

const detailDefaults: Record<DetailKind, string> = {
  retained: 'Returning',
  churned: 'Stopped',
  new: 'New',
  reactivated: 'Came back',
};

const ACTIVE = '#f59e0b';
const RETURNING = '#10b981';
const NEW_COLOR = '#6366f1';
const CAME_BACK = '#0ea5e9';
const STOPPED = '#e11d48';
const COHORT_COLORS = ['#6366f1', '#0ea5e9', '#10b981', '#f59e0b', '#e11d48', '#8b5cf6'];

const kindAccent: Record<DetailKind, string> = {
  retained: RETURNING,
  new: NEW_COLOR,
  reactivated: CAME_BACK,
  churned: STOPPED,
};

function formatMonth(month: string, language: string): string {
  const [year, monthNumber] = month.split('-').map(Number);
  if (!year || !monthNumber) return month;
  return new Intl.DateTimeFormat(language, { month: 'short', year: 'numeric' })
    .format(new Date(Date.UTC(year, monthNumber - 1, 1)));
}

function previousMonthKey(month: string): string | null {
  const [year, monthNumber] = month.split('-').map(Number);
  if (!year || !monthNumber) return null;
  const date = new Date(Date.UTC(year, monthNumber - 2, 1));
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`;
}

function formatDateTime(value: string | null, language: string): string {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : new Intl.DateTimeFormat(language, {
    dateStyle: 'medium', timeStyle: 'short',
  }).format(date);
}

function formatRate(value: number): string {
  return `${Number(value).toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;
}

function formatSignedCount(value: number): string {
  const rounded = Math.round(value);
  return rounded > 0 ? `+${rounded}` : String(rounded);
}

function axisMax(values: number[]): number {
  const max = Math.max(0, ...values);
  return max <= 0 ? 1 : Math.ceil(max * 1.2);
}

function SeriesLegend({ items }: { items: { label: string; color: string }[] }) {
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 1.5 }}>
      {items.map((item) => (
        <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: item.color, flexShrink: 0 }} />
          <Typography variant="caption">{item.label}</Typography>
        </Box>
      ))}
    </Box>
  );
}

function MetricTile({
  label, value, hint, note, accent, onClick, actionLabel, actionText,
}: {
  label: string;
  value: React.ReactNode;
  hint: string;
  note?: string;
  accent: string;
  onClick?: () => void;
  actionLabel?: string;
  actionText?: string;
}) {
  return (
    <Card
      variant="outlined"
      component={onClick ? 'button' : 'div'}
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      aria-label={actionLabel}
      sx={{
        p: 2,
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        textAlign: 'left',
        borderRadius: 2,
        color: 'inherit',
        font: 'inherit',
        bgcolor: 'background.paper',
        borderColor: 'divider',
        ...(onClick ? { cursor: 'pointer', '&:hover': { borderColor: accent } } : {}),
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: accent, flexShrink: 0 }} />
        <Typography variant="body2" color="text.secondary">{label}</Typography>
      </Box>
      <Typography variant="h4" fontWeight={700} sx={{ mt: 1, lineHeight: 1.1 }}>{value}</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>{hint}</Typography>
      {note && <Typography variant="caption" color="info.main" sx={{ display: 'block', mt: 0.5 }}>{note}</Typography>}
      {onClick && actionText && (
        <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 'auto', pt: 1.25 }}>
          {actionText}
        </Typography>
      )}
    </Card>
  );
}

export default function GrowthRetentionPanels({ data }: { data: GrowthSummary }) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const [dialog, setDialog] = useState<DetailDialogState | null>(null);
  const monthly = useMemo(
    () => [...(data.roomBookingRetentionByMonth ?? [])].sort((a, b) => a.month.localeCompare(b.month)),
    [data.roomBookingRetentionByMonth],
  );
  const cohorts = data.roomBookingCohortRetention ?? [];
  const rolling = [...(data.roomBookingRollingRetention ?? [])].sort((a, b) => a.windowDays - b.windowDays);
  const latest = monthly[monthly.length - 1];
  const maxCohortPeriod = Math.max(-1, ...cohorts.flatMap((cohort) => cohort.periods.map((period) => period.monthsSinceFirstBooking)));
  const language = i18n.language || 'en';
  const chartCohorts = useMemo(
    () => [...cohorts].sort((a, b) => a.cohortMonth.localeCompare(b.cohortMonth)).slice(-COHORT_COLORS.length),
    [cohorts],
  );

  const detailLabel = (kind: DetailKind) => t(`growth.retention.${kind}`, detailDefaults[kind]);
  const viewPeopleLabel = t('growth.retention.viewPeople', 'View people');

  const openDetails = (row: RoomBookingMonthlyRetention, kind: DetailKind) => {
    const users = row[detailConfig[kind].detailsKey] as RoomBookingRetentionUser[];
    setDialog({ kind, month: row.month, users: users ?? [] });
  };

  const countButton = (row: RoomBookingMonthlyRetention, kind: DetailKind) => {
    const count = row[detailConfig[kind].countKey] as number;
    const users = (row[detailConfig[kind].detailsKey] as RoomBookingRetentionUser[]) ?? [];
    return users.length > 0 ? (
      <Button size="small" onClick={() => openDetails(row, kind)} aria-label={`${detailLabel(kind)} ${formatMonth(row.month, language)}`}>
        {count}
      </Button>
    ) : count;
  };

  const chartFrameSx = {
    width: '100%',
    minWidth: 0,
    overflowX: 'auto',
    '& .MuiChartsAxis-tickLabel': { fontSize: '0.75rem' },
    '& .MuiChartsAxis-line': { stroke: theme.palette.divider },
  };

  if (!monthly.length && !cohorts.length && !rolling.length) {
    return (
      <Card variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6">{t('growth.retention.title', 'Room booking retention')}</Typography>
        <Typography color="text.secondary" sx={{ mt: 1 }}>
          {t('growth.retention.noData', 'No room booking retention data is available yet.')}
        </Typography>
      </Card>
    );
  }

  const priorKey = latest ? previousMonthKey(latest.month) : null;
  const priorLabel = priorKey ? formatMonth(priorKey, language) : t('growth.retention.previousMonth', 'the previous month');
  const activeDelta = latest ? latest.currentMonthActiveUsers - latest.previousMonthActiveUsers : 0;
  const mix = latest ? [
    { key: 'retained' as const, count: latest.retainedUsers, color: RETURNING },
    { key: 'new' as const, count: latest.newUsers, color: NEW_COLOR },
    { key: 'reactivated' as const, count: latest.reactivatedUsers, color: CAME_BACK },
  ] : [];
  const mixTotal = mix.reduce((sum, part) => sum + part.count, 0);
  const activeTotals = monthly.map((row) => row.retainedUsers + row.newUsers + row.reactivatedUsers);
  const monthLabels = monthly.map((row) => formatMonth(row.month, language));
  const rollingColors = [RETURNING, NEW_COLOR, ACTIVE];

  const groupHint: Record<DetailKind, string> = {
    retained: t('growth.retention.returningHint', 'Booked last month and again this month'),
    new: t('growth.retention.newHint', 'First completed room booking'),
    reactivated: t('growth.retention.cameBackHint', 'Booked again after skipping last month'),
    churned: t('growth.retention.stoppedHint', 'Booked last month, not this month'),
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5">{t('growth.retention.title', 'Room booking retention')}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {t('growth.retention.description', 'Month-to-month activity based on completed, confirmed room bookings.')}
        </Typography>
      </Box>

      {latest && !latest.finalized && (
        <Alert severity="info" sx={{ mb: 3 }}>
          {t('growth.retention.mtdNotice', 'The latest month is month-to-date and may change as more sessions end.')}
        </Alert>
      )}

      {latest && (
        <>
          <Grid container spacing={2} sx={{ mb: 2 }} alignItems="stretch">
            <Grid item xs={6} sm={6} md={4}>
              <MetricTile
                label={t('growth.retention.currentActive', 'Active bookers')}
                value={latest.currentMonthActiveUsers}
                accent={ACTIVE}
                hint={t('growth.retention.vsLastMonth', '{{delta}} vs {{month}}', {
                  delta: formatSignedCount(activeDelta),
                  month: priorLabel,
                })}
                note={latest.finalized ? undefined : t('growth.retention.mtd', 'MTD')}
              />
            </Grid>
            {(['retained', 'new', 'reactivated', 'churned'] as DetailKind[]).map((kind) => {
              const users = (latest[detailConfig[kind].detailsKey] as RoomBookingRetentionUser[]) ?? [];
              const count = latest[detailConfig[kind].countKey] as number;
              const open = users.length > 0 ? () => openDetails(latest, kind) : undefined;
              return (
                <Grid item xs={6} sm={6} md={4} key={kind}>
                  <MetricTile
                    label={detailLabel(kind)}
                    value={count}
                    accent={kindAccent[kind]}
                    hint={groupHint[kind]}
                    onClick={open}
                    actionText={open ? viewPeopleLabel : undefined}
                    actionLabel={open ? t('growth.retention.viewGroup', 'View {{count}} {{state}} users for {{month}}', {
                      count,
                      state: detailLabel(kind),
                      month: formatMonth(latest.month, language),
                    }) : undefined}
                  />
                </Grid>
              );
            })}
            <Grid item xs={6} sm={6} md={4}>
              <MetricTile
                label={t('growth.retention.retentionRate', 'Retention rate')}
                value={formatRate(latest.retentionRatePercentage)}
                accent={RETURNING}
                hint={t('growth.retention.churnBeside', 'Churn {{rate}}', { rate: formatRate(latest.churnRatePercentage) })}
                note={latest.finalized ? undefined : t('growth.retention.notFinal', 'Not final yet')}
              />
            </Grid>
          </Grid>

          <Card variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              {t('growth.retention.mixTitle', 'Who booked in {{month}}', { month: formatMonth(latest.month, language) })}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1.5 }}>
              {t('growth.retention.mixDescription', 'Active bookers split into returning, new, and people who came back.')}
            </Typography>
            <Box sx={{ display: 'flex', height: 14, borderRadius: 99, overflow: 'hidden', bgcolor: 'action.hover' }}>
              {mixTotal > 0 && mix.filter((part) => part.count > 0).map((part) => (
                <Box key={part.key} sx={{ flex: part.count, bgcolor: part.color }} />
              ))}
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1.5 }}>
              {mix.map((part) => (
                <Box key={part.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: part.color }} />
                  <Typography variant="body2">{part.count} {detailLabel(part.key)}</Typography>
                </Box>
              ))}
              <Typography variant="body2" color="text.secondary">
                {t('growth.retention.lastMonthActive', '{{count}} active in {{month}}', {
                  count: latest.previousMonthActiveUsers,
                  month: priorLabel,
                })}
              </Typography>
            </Box>
          </Card>
        </>
      )}

      {monthly.length > 0 && (
        <Card variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
          <Typography variant="h6">{t('growth.retention.activeTrend', 'Active bookers by month')}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
            {t('growth.retention.activeTrendDescription', 'Each bar is people with a completed room booking, split by how they relate to earlier months.')}
          </Typography>
          <SeriesLegend items={[
            { label: detailLabel('retained'), color: RETURNING },
            { label: detailLabel('new'), color: NEW_COLOR },
            { label: detailLabel('reactivated'), color: CAME_BACK },
          ]} />
          <Box sx={chartFrameSx}>
            <Box sx={{ minWidth: Math.max(520, monthly.length * 72) }}>
              <BarChart
                xAxis={[{ scaleType: 'band', data: monthLabels }]}
                yAxis={[{ min: 0, max: axisMax(activeTotals) }]}
                series={[
                  { data: monthly.map((row) => row.retainedUsers), label: detailLabel('retained'), stack: 'active', color: RETURNING },
                  { data: monthly.map((row) => row.newUsers), label: detailLabel('new'), stack: 'active', color: NEW_COLOR },
                  { data: monthly.map((row) => row.reactivatedUsers), label: detailLabel('reactivated'), stack: 'active', color: CAME_BACK },
                ]}
                grid={{ horizontal: true }}
                slotProps={{ legend: { hidden: true } }}
                skipAnimation
                height={300}
                margin={{ top: 16, right: 12, bottom: 40, left: 48 }}
              />
            </Box>
          </Box>
        </Card>
      )}

      {rolling.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6">{t('growth.retention.rolling', 'Rolling retention')}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
            {t('growth.retention.rollingDescription', 'People who booked again within 30, 60, or 90 days of their first completed session.')}
          </Typography>
          <Grid container spacing={2}>
            {rolling.map((row, index) => (
              <Grid item xs={12} sm={4} key={row.windowDays}>
                <Paper variant="outlined" sx={{ p: 2, height: '100%', borderRadius: 2 }}>
                  <Typography color="text.secondary">{t('growth.retention.dayWindow', '{{days}}-day window', { days: row.windowDays })}</Typography>
                  <Typography variant="h4" fontWeight={700} sx={{ my: 0.5 }}>{formatRate(row.retentionRatePercentage)}</Typography>
                  <Typography variant="body2">
                    {t('growth.retention.retainedOfEligible', '{{retained}} of {{eligible}} eligible users retained', {
                      retained: row.retainedUsers,
                      eligible: row.eligibleUsers,
                    })}
                  </Typography>
                  {row.eligibleUsers === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {t('growth.retention.rollingEmpty', 'No users have completed this full window yet.')}
                    </Typography>
                  )}
                  <LinearProgress
                    variant="determinate"
                    value={Math.max(0, Math.min(100, Number(row.retentionRatePercentage) || 0))}
                    sx={{
                      mt: 2,
                      height: 8,
                      borderRadius: 99,
                      bgcolor: 'action.hover',
                      '& .MuiLinearProgress-bar': { borderRadius: 99, bgcolor: rollingColors[index % rollingColors.length] },
                    }}
                  />
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {cohorts.length > 0 && (
        <Card variant="outlined" sx={{ mb: 3, overflow: 'hidden', borderRadius: 2 }}>
          <Box sx={{ p: 2 }}>
            <Typography variant="h6">{t('growth.retention.cohortCurve', 'Return curve')}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {t('growth.retention.cohortCurveDescription', 'Each line is people who first booked in that month. It shows the share who booked again in later months.')}
            </Typography>
            {chartCohorts.length < cohorts.length && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {t('growth.retention.cohortChartLimit', 'The chart shows the latest {{count}} groups. The table lists every group.', { count: chartCohorts.length })}
              </Typography>
            )}
          </Box>
          {maxCohortPeriod >= 0 && (
            <Box sx={{ px: 2, pb: 1 }}>
              <SeriesLegend items={chartCohorts.map((cohort, index) => ({
                label: formatMonth(cohort.cohortMonth, language),
                color: COHORT_COLORS[index % COHORT_COLORS.length],
              }))} />
              <Box sx={chartFrameSx}>
                <Box sx={{ minWidth: Math.max(520, (maxCohortPeriod + 1) * 72) }}>
                  <LineChart
                    xAxis={[{
                      scaleType: 'band',
                      data: Array.from({ length: maxCohortPeriod + 1 }, (_, index) => `M${index}`),
                    }]}
                    yAxis={[{ min: 0, max: 100 }]}
                    series={chartCohorts.map((cohort, index) => {
                      const periods = new Map(cohort.periods.map((period) => [period.monthsSinceFirstBooking, period.retentionRatePercentage]));
                      return {
                        data: Array.from({ length: maxCohortPeriod + 1 }, (_, periodIndex) => periods.get(periodIndex) ?? null),
                        label: formatMonth(cohort.cohortMonth, language),
                        color: COHORT_COLORS[index % COHORT_COLORS.length],
                        curve: 'linear' as const,
                        showMark: true,
                        valueFormatter: (value: number | null) => (value == null ? '' : formatRate(value)),
                      };
                    })}
                    grid={{ horizontal: true }}
                    slotProps={{ legend: { hidden: true } }}
                    skipAnimation
                    height={300}
                    margin={{ top: 16, right: 16, bottom: 40, left: 48 }}
                  />
                </Box>
              </Box>
            </Box>
          )}
          <Box sx={{ px: 2, pt: 1, pb: 0.5 }}>
            <Typography variant="subtitle1" fontWeight={600}>{t('growth.retention.cohorts', 'Cohort retention')}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {t('growth.retention.cohortDescription', 'Users grouped by the month of their first completed room booking.')}
            </Typography>
          </Box>
          <TableContainer sx={{ overflowX: 'auto' }}><Table size="small" sx={{ minWidth: 460 + Math.max(maxCohortPeriod, 0) * 90 }}>
            <TableHead><TableRow><TableCell>{t('growth.retention.cohort', 'Cohort')}</TableCell><TableCell align="right">{t('growth.retention.cohortSize', 'Size')}</TableCell>
              {Array.from({ length: maxCohortPeriod + 1 }, (_, index) => <TableCell align="center" key={index}>M{index}</TableCell>)}
            </TableRow></TableHead>
            <TableBody>{cohorts.map((cohort) => {
              const periods = new Map(cohort.periods.map((period) => [period.monthsSinceFirstBooking, period]));
              return <TableRow key={cohort.cohortMonth}>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatMonth(cohort.cohortMonth, language)}</TableCell>
                <TableCell align="right">{cohort.cohortSize}</TableCell>
                {Array.from({ length: maxCohortPeriod + 1 }, (_, index) => {
                  const period = periods.get(index);
                  return <TableCell key={index} align="center" sx={period ? { bgcolor: theme.palette.success.main, color: theme.palette.success.contrastText, opacity: 0.3 + Math.min(period.retentionRatePercentage, 100) / 145 } : undefined}>
                    {period ? <Box sx={{ whiteSpace: 'nowrap' }}><strong>{formatRate(period.retentionRatePercentage)}</strong><Typography component="div" variant="caption">{period.retainedUsers}</Typography>{!period.finalized && <Typography component="div" variant="caption">{t('growth.retention.mtd', 'MTD')}</Typography>}</Box> : '—'}
                  </TableCell>;
                })}
              </TableRow>;
            })}</TableBody>
          </Table></TableContainer>
        </Card>
      )}

      {monthly.length > 0 && (
        <Card variant="outlined" sx={{ mb: 3, overflow: 'hidden', borderRadius: 2 }}>
          <Box sx={{ p: 2 }}>
            <Typography variant="h6">{t('growth.retention.monthly', 'Monthly retention')}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t('growth.retention.monthlyDescription', 'Every month, with the people behind each count.')}
            </Typography>
          </Box>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 1050 }}>
              <TableHead><TableRow>
                <TableCell>{t('growth.retention.month', 'Month')}</TableCell>
                <TableCell align="right">{t('growth.retention.previousActive', 'Active last month')}</TableCell>
                <TableCell align="right">{t('growth.retention.currentActive', 'Active bookers')}</TableCell>
                {(['retained', 'churned', 'new', 'reactivated'] as DetailKind[]).map((kind) => <TableCell align="right" key={kind}>{detailLabel(kind)}</TableCell>)}
                <TableCell align="right">{t('growth.retention.retentionRate', 'Retention rate')}</TableCell>
                <TableCell align="right">{t('growth.retention.churnRate', 'Churn rate')}</TableCell>
              </TableRow></TableHead>
              <TableBody>{monthly.map((row) => (
                <TableRow key={row.month} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, whiteSpace: 'nowrap' }}>
                      {formatMonth(row.month, language)}
                      <Chip size="small" color={row.finalized ? 'default' : 'info'} label={row.finalized ? t('growth.retention.final', 'Final') : t('growth.retention.mtd', 'MTD')} />
                    </Box>
                  </TableCell>
                  <TableCell align="right">{row.previousMonthActiveUsers}</TableCell>
                  <TableCell align="right">{row.currentMonthActiveUsers}</TableCell>
                  {(['retained', 'churned', 'new', 'reactivated'] as DetailKind[]).map((kind) => <TableCell align="right" key={kind}>{countButton(row, kind)}</TableCell>)}
                  <TableCell align="right">{formatRate(row.retentionRatePercentage)}</TableCell>
                  <TableCell align="right">{formatRate(row.churnRatePercentage)}</TableCell>
                </TableRow>
              ))}</TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      <Dialog open={dialog !== null} onClose={() => setDialog(null)} fullWidth maxWidth="md">
        <DialogTitle>{dialog && t('growth.retention.dialogTitle', '{{state}} users · {{month}}', { state: detailLabel(dialog.kind), month: formatMonth(dialog.month, language) })}</DialogTitle>
        <DialogContent dividers>
          {dialog && dialog.users.length > 0 ? <TableContainer><Table size="small" sx={{ minWidth: 720 }}>
            <TableHead><TableRow>
              <TableCell>{t('growth.retention.user', 'User')}</TableCell><TableCell>{t('growth.retention.phone', 'Phone')}</TableCell><TableCell>{t('growth.retention.previousSession', 'Previous month last session')}</TableCell><TableCell>{t('growth.retention.currentSession', 'Current month last session')}</TableCell><TableCell>{t('growth.retention.userId', 'User ID')}</TableCell>
            </TableRow></TableHead>
            <TableBody>{dialog.users.map((user) => <TableRow key={user.userId}>
              <TableCell>{user.name || t('growth.retention.unnamed', 'Unnamed user')}</TableCell><TableCell sx={{ whiteSpace: 'nowrap' }}>{user.phoneNumber}</TableCell><TableCell>{formatDateTime(user.previousMonthLastSessionAt, language)}</TableCell><TableCell>{formatDateTime(user.currentMonthLastSessionAt, language)}</TableCell><TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{user.userId}</TableCell>
            </TableRow>)}</TableBody>
          </Table></TableContainer> : <Typography color="text.secondary">{t('growth.retention.noUsers', 'No users in this group.')}</Typography>}
        </DialogContent>
        <DialogActions><Button onClick={() => setDialog(null)}>{t('common.close', 'Close')}</Button></DialogActions>
      </Dialog>
    </Box>
  );
}
